import React, { Suspense, useContext, useEffect, useMemo } from 'react'

import { Header } from '@mantine/core'
import { Alert } from 'antd'
import { I18nContext } from 'react-i18next'
import { useParams } from 'react-router-dom'
import useSWR from 'swr'

import { getClusterInfo, getTrafficMirrorReport } from '~/api'
import TopBar from '~/components/Layout/TopBar'
import LoadingSpin from '~/components/LoadingSpin'
import { useCurOrg } from '~/hooks/state'
import { RemoteNamespace } from '~/translations'
import { formatDateTime } from '~/utils/date-time'

import { OrgAndClusterContext } from '../shared/context'
import { BasicConfiguration } from './BasicConfiguration'
import { MetricChartComparison } from './MetricChartComparison/MetricChartComparison'
import { MetricsSummary } from './MetricsSummary'
import { SlowQueryComparison } from './SlowQueryComparison'
import { TopSQLComparison } from './TopSQLComparison'

const TrafficMirrorReport: React.FC = () => {
  const { orgId, cid, rid } = useParams<'orgId' | 'cid' | 'rid'>()

  // 使用 mock 数据或真实 API
  const { data, error } = useSWR(
    ['traffic_mirror_report/detail', orgId, cid, rid],
    () => getTrafficMirrorReport(orgId!, cid!, rid!)
  )

  // Mock 数据或真实数据
  const d = data?.data
  const clusterID1 = d?.clusterID1
  const clusterID2 = d?.clusterID2

  const { data: clusterInfo1, error: clusterInfo1Error } = useSWR(
    `/orgs/${orgId}/clusters/${clusterID1}`,
    () => getClusterInfo(orgId!, clusterID1!, { skipErrorHandler: true })
  )
  const { data: clusterInfo2, error: clusterInfo2Error } = useSWR(
    `/orgs/${orgId}/clusters/${clusterID2}`,
    () => getClusterInfo(orgId!, clusterID2!, { skipErrorHandler: true })
  )

  const { curOrg } = useCurOrg()

  const orgAndCluster = useMemo(
    () => ({
      isLoading:
        (!clusterInfo1 && !clusterInfo1Error) ||
        (!clusterInfo2 && !clusterInfo2Error) ||
        !curOrg,
      orgId: curOrg?.id || 'mock-org-id',
      orgName: curOrg?.name || 'Mock Organization',
      cluster1: clusterInfo1?.data,
      cluster2: clusterInfo2?.data
    }),
    [curOrg, clusterInfo1, clusterInfo2, clusterInfo1Error, clusterInfo2Error]
  )

  return (
    <OrgAndClusterContext.Provider value={orgAndCluster}>
      <div className='h-full flex flex-col overflow-auto'>
        <Header height={56} px={24}>
          <TopBar />
        </Header>

        <main className='flex-auto m-auto w-full h-full py-6'>
          <article className='px-8 py-8 min-h-full bg-white'>
            <header className='mb-8'>
              <h1 className='text-4xl font-semibold'>
                Performance Comparison Report
              </h1>
              {data?.data?.createdAt && (
                <p className='text-gray-500 text-lg mt-2'>
                  Generated Time: {formatDateTime(data.data.createdAt)}
                </p>
              )}
            </header>

            {(error || (d as any)?.errorMessage) && (
              <Alert
                message={
                  (d as any)?.errorMessage ||
                  error?.response?.data?.message ||
                  error.message
                }
                type='error'
                showIcon
                className='mb-4'
              />
            )}

            {!data && !error ? (
              <LoadingSpin />
            ) : (
              !!d && (
                <>
                  <BasicConfiguration
                    data={d}
                    clusterInfo1={clusterInfo1?.data}
                    clusterInfo2={clusterInfo2?.data}
                  />
                  <MetricsSummary data={d.data?.metricsDiff as any} />
                  <SlowQueryComparison data={d.data?.sqlDiff} />
                  <TopSQLComparison data={d.data?.sqlDiff} />
                  <MetricChartComparison data={d.data?.metricsDiff} />
                </>
              )
            )}
          </article>
        </main>
      </div>
    </OrgAndClusterContext.Provider>
  )
}

export const TrafficMirrorReportDetail: React.FC = () => {
  const { i18n } = useContext(I18nContext) || {}

  // Load i18n remote resources for comparison report
  useEffect(() => {
    i18n?.loadNamespaces([RemoteNamespace.MetricsCompare])
  }, [i18n])

  return (
    <Suspense fallback={<LoadingSpin />}>
      <TrafficMirrorReport />
    </Suspense>
  )
}
